import { API_URL, api, requestEnvelope } from "./apiClient";

export type UserType = "INTERNAL" | "AGENT" | "SUB_AGENT" | "GODADDY_MERCHANT";
export type UserStatus = "INVITED" | "ACTIVE" | "DISABLED";
export type AccessRequestStatus = "PENDING" | "APPROVED" | "DENIED";

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  userType: UserType;
  status: UserStatus;
  createdAt: string;
  roles: string[];
  /** True when an admin has assigned a temporary password and the user has
   *  not yet picked their own. Drives the /force-password-reset interstitial. */
  mustResetPassword: boolean;
  /** Deadline (ISO) until which the user can still skip the forced reset.
   *  Past this point the Skip button is hidden and reset becomes mandatory. */
  tempPasswordGraceUntil: string | null;
  /** Whether email-based 2FA is enabled for this account. */
  twoFactorEnabled: boolean;
  impersonating: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    userType: UserType;
    createdAt: string;
  } | null;
}

/** Returned by sign-in when the account has 2FA enabled — no session yet. */
export interface TwoFactorRequired {
  requires2fa: true;
  challengeId: string;
  email: string;
}

/** Type guard: did sign-in return a 2FA challenge instead of a user? */
export function isTwoFactorRequired(
  result: CurrentUser | TwoFactorRequired,
): result is TwoFactorRequired {
  return (result as TwoFactorRequired).requires2fa === true;
}

export interface UpdateMeInput {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
}

export interface UserListItem {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  userType: UserType;
  status: UserStatus;
  parentAgentId?: string | null;
  roles: string[];
  // Not served yet; commissions show them once they are.
  nickname?: string | null;
  companyName?: string | null;
}

export interface AccessRequest {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  reason: string | null;
  requestedUserType: UserType | null;
  status: AccessRequestStatus;
  reviewedAt: string | null;
  reviewedBy: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  denialReason: string | null;
  /** The account approval created; null until approved. */
  createdUserId: string | null;
  createdAt: string;
}

export const authApi = {
  me: () => api.get<CurrentUser>("/api/auth/me"),
  updateMe: (body: UpdateMeInput) =>
    api.patch<CurrentUser>("/api/auth/me", body),
  // Returns the user on success, or a 2FA challenge when the account has email
  // 2FA enabled (use isTwoFactorRequired to discriminate).
  signIn: (email: string, password: string) =>
    api.post<CurrentUser | TwoFactorRequired>("/api/auth/sign-in", {
      email,
      password,
    }),
  // Complete a 2FA-gated sign-in. On success the session cookie is set and the
  // user is returned.
  verifyTwoFactor: (challengeId: string, code: string) =>
    api.post<CurrentUser>("/api/auth/verify-2fa", { challengeId, code }),
  // Re-send the login code for an in-flight challenge (returns the new id).
  resendTwoFactor: (challengeId: string) =>
    api.post<{ challengeId: string }>("/api/auth/resend-2fa", { challengeId }),
  // Account → Security: emails a confirmation code; `purpose` reflects whether
  // confirming will turn 2FA on or off (derived from current state server-side).
  setupTwoFactor: () =>
    api.post<{ challengeId: string; purpose: "ENABLE" | "DISABLE" }>(
      "/api/auth/2fa/setup",
    ),
  // Verify the setup code and flip the flag; returns the new state.
  confirmTwoFactor: (challengeId: string, code: string) =>
    api.post<{ twoFactorEnabled: boolean }>("/api/auth/2fa/confirm", {
      challengeId,
      code,
    }),
  setPassword: (token: string, password: string) =>
    api.post<{ ok: boolean }>("/api/auth/set-password", { token, password }),
  requestPasswordReset: (email: string) =>
    api.post<{ ok: boolean }>("/api/auth/request-password-reset", { email }),
  // `currentPassword` is omitted in the forced-reset flow (user is already
  // signed in with the temp password). Required for normal account-settings
  // rotations — the backend rejects if missing in that mode.
  changePassword: (newPassword: string, currentPassword?: string) =>
    api.post<{ ok: boolean }>("/api/auth/change-password", {
      newPassword,
      ...(currentPassword !== undefined ? { currentPassword } : {}),
    }),
  logout: () =>
    api.post<{ logoutUrl?: string; message?: string }>("/api/auth/logout"),
  // Legacy WorkOS hosted UI URL — kept for SSO flow later.
  loginUrl: () => `${API_URL}/api/auth/login`,
};

export interface UsersListParams {
  search?: string;
  /** User types (INTERNAL/AGENT/SUB_AGENT/GODADDY_MERCHANT). */
  type?: string[];
  /** User statuses (ACTIVE/INVITED/DISABLED). */
  status?: string[];
  includeDisabled?: boolean;
  page?: number;
  limit?: number;
}

export interface UsersListResponse {
  data: UserListItem[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

function buildUsersQuery(params: UsersListParams): string {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.includeDisabled) sp.set("includeDisabled", "true");
  if (params.page) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.type?.length) sp.set("type", params.type.join(","));
  if (params.status?.length) sp.set("status", params.status.join(","));
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const userApi = {
  list: (params: { includeDisabled?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.includeDisabled) qs.set("includeDisabled", "true");
    const q = qs.toString();
    return api.get<UserListItem[]>(`/api/users${q ? `?${q}` : ""}`);
  },
  // Server-paginated + server-filtered variant; keeps the { data, meta } envelope
  // so the admin table can show the total and page through results.
  listPaged: (params: UsersListParams = {}) =>
    requestEnvelope<UsersListResponse>({
      path: `/api/users${buildUsersQuery(params)}`,
      method: "GET",
    }),
  listAgents: () => api.get<UserListItem[]>("/api/users/agents"),
  create: (body: {
    email: string;
    firstName?: string;
    lastName?: string;
    /** Optional — derived server-side from the account role in `roles`.
     *  AGENT callers may omit it (the backend forces SUB_AGENT). */
    userType?: UserType;
    /** Account role + any permission roles. AGENT callers may omit (backend
     *  forces a sub_agent). Internal creates pass exactly one account role. */
    roles?: string[];
    /** Required when an INTERNAL admin creates a SUB_AGENT. */
    parentAgentId?: string | null;
  }) => api.post<UserListItem>("/api/users", body),
  update: (
    userId: string,
    body: {
      firstName?: string;
      lastName?: string;
      userType?: UserType;
      status?: "ACTIVE" | "DISABLED";
      parentAgentId?: string | null;
    },
  ) => api.patch<UserListItem>(`/api/users/${userId}`, body),
  updateRoles: (userId: string, roles: string[]) =>
    api.patch<UserListItem>(`/api/users/${userId}/roles`, { roles }),
  resendInvite: (userId: string) =>
    api.post<{ sent: boolean }>(`/api/users/${userId}/resend-invite`),
  sendLink: (userId: string) =>
    api.post<{ sent: boolean }>(`/api/users/${userId}/send-link`),
  setTempPassword: (userId: string, password: string) =>
    api.post<{ ok: boolean; graceUntil: string }>(
      `/api/users/${userId}/set-temp-password`,
      { password },
    ),
  remove: (userId: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/api/users/${userId}`),
  startImpersonation: (userId: string) =>
    api.post<{
      sessionId: string;
      impersonatedUserId: string;
      expiresAt: string;
    }>(`/api/users/${userId}/impersonate`),
  endImpersonation: () =>
    api.post<{ message: string }>("/api/impersonation/end"),
};
